import { useState, type ReactNode } from 'react';
import { Check, Copy, X } from 'lucide-react';
import StatusBadge from '../../../components/shared/StatusBadge';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { Tooltip, TooltipContent, TooltipTrigger, TOOLTIP_HINT_CLASS } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { copyToClipboard } from '../../../utils/clipboard';
import { formatCurrency } from '../../../utils/helpers';
import { formatEntityStatusLabel, getOrderStatusLabel } from '../../../utils/status';
import type { TransactionDccDetail } from '../models/types';
import type { MappedOrder } from '../utils/utils';
import { formatPaymentMethodLabel } from '../utils/utils';

type RelatedTransaction = {
  transactionId?: string;
  id?: string;
  status?: string;
  paymentMethod?: string;
  amount?: number | string;
  currency?: string;
};

type TransactionDetailDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: MappedOrder | null;
  isMerchantUser: boolean;
  loadingRelated: boolean;
  relatedTransactions: RelatedTransaction[];
  dccDetails: Record<string, TransactionDccDetail>;
};

const TRACE_LABELS: Record<string, string> = {
  S2S_CALLER_IP: 'Caller IP',
  S2S_REVERSE_DNS: 'Server hostname (reverse DNS)',
  DECLARED_STOREFRONT: 'Declared storefront URL',
  S2S_ORIGIN_HEADER: 'Origin header',
  S2S_REFERER_HEADER: 'Referer header',
  PROXY_BROWSER_IP: 'Hosted-page browser IP',
  REFERER: 'Referrer',
  ANCESTOR_ORIGIN: 'Embedding page (ancestor origin)',
  BROWSER_DOCUMENT_REFERRER: 'Browser referrer',
  THREE_DS_BROWSER_IP: '3DS browser IP',
  THREE_DS_ANCESTOR_ORIGIN: '3DS embedding page',
  THREE_DS_REFERER: '3DS referrer',
  THREE_DS_USER_AGENT: '3DS user agent',
  THREE_DS_DEVICE_CONTEXT: '3DS device context',
};

const COPY_TRACE_TYPES = new Set(['THREE_DS_REFERER', 'THREE_DS_USER_AGENT']);

const FIELD_TRACE_TYPES = new Set([
  'S2S_CALLER_IP',
  'THREE_DS_BROWSER_IP',
  'PROXY_BROWSER_IP',
]);

const DCC_TABLE_HEADERS = [
  'Base Currency',
  'Converted Currency',
  'Base Amount',
  'Converted Amount',
] as const;

function hasText(value?: string | null) {
  const text = String(value ?? '').trim();
  return text.length > 0 && text !== '—';
}

function hostOf(url?: string | null) {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        copied && 'text-emerald-600 hover:text-emerald-600',
      )}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      onClick={async (event) => {
        event.stopPropagation();
        const ok = await copyToClipboard(value);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CopyValue({
  value,
  label,
  children,
}: {
  value: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex max-w-full min-w-0 items-center gap-1">
      <span className="min-w-0">{children}</span>
      <CopyButton value={value} label={label} />
    </span>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children?: ReactNode;
  wide?: boolean;
}) {
  const empty = children == null || children === '' || children === '—';
  return (
    <div className={cn('min-w-0 space-y-1', wide && 'sm:col-span-2')}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium break-words text-foreground">{empty ? '—' : children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-b border-border py-5 last:border-b-0">
      <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function UrlText({ url }: { url: string }) {
  const segments = url.split('/');
  return segments.map((segment, index) => (
    <span key={`${segment}-${index}`}>
      {index > 0 && (
        <>
          /<wbr />
        </>
      )}
      {segment}
    </span>
  ));
}

function UrlList({
  urls,
  empty,
  copyLabel,
}: {
  urls: string[];
  empty: string;
  copyLabel?: string;
}) {
  if (!urls.length) return <span className="font-normal text-muted-foreground">{empty}</span>;
  return (
    <span className="flex flex-col gap-1">
      {urls.map((url) => {
        const link = (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline-offset-4 hover:underline"
          >
            <UrlText url={url} />
          </a>
        );
        return copyLabel ? (
          <CopyValue key={url} value={url} label={copyLabel}>
            {link}
          </CopyValue>
        ) : (
          <span key={url}>{link}</span>
        );
      })}
    </span>
  );
}

function DccDetailTable({ dcc }: { dcc: TransactionDccDetail }) {
  if (!dcc.dccApplied) return null;

  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="bg-muted/40">
            {DCC_TABLE_HEADERS.map((header) => (
              <th key={header} className="px-3 py-2 text-xs font-medium text-muted-foreground">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-3 py-2 text-sm">{dcc.originalCurrency}</td>
            <td className="px-3 py-2 text-sm">{dcc.convertedCurrency}</td>
            <td className="px-3 py-2 text-sm">
              {formatCurrency(Number(dcc.originalAmount), dcc.originalCurrency)}
            </td>
            <td className="px-3 py-2 text-sm">
              {formatCurrency(Number(dcc.convertedAmount), dcc.convertedCurrency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function buildOrigin(order: MappedOrder) {
  const sources = (order.trafficSources ?? []).filter((source) => source.value);
  const byType = (type: string) => sources.find((source) => source.type === type)?.value || '';
  const registered = order.registeredStoreUrls ?? [];
  const observed = order.trafficSourceUrl || '';
  const shopperIp = byType('THREE_DS_BROWSER_IP') || byType('PROXY_BROWSER_IP');
  const observedHost = hostOf(observed);
  const registeredHosts = registered.map(hostOf).filter(Boolean);
  const haveBoth = !!observedHost && registeredHosts.length > 0;
  const matched = haveBoth && registeredHosts.includes(observedHost);

  let message = 'Origin recorded.';
  let tone: 'ok' | 'bad' | 'neutral' = 'neutral';
  if (haveBoth && matched) {
    message = 'Referrer matches the registered URL.';
    tone = 'ok';
  } else if (haveBoth && !matched) {
    message = 'Referrer differs from the registered URL.';
    tone = 'bad';
  } else if (!observed && sources.length === 0) {
    message = 'No URL/referrer — direct API call (no browser origin to trace).';
  } else if (!observed) {
    message = 'No browser origin — direct S2S call.';
  } else if (registered.length === 0) {
    message = 'Observed origin recorded. No approved URL on this terminal.';
  }

  const shownValues = new Set(
    [observed, order.callerIp, shopperIp, ...registered].filter(Boolean),
  );
  const extras = sources.filter((source) => {
    if (FIELD_TRACE_TYPES.has(source.type)) return false;
    if (COPY_TRACE_TYPES.has(source.type)) return true;
    if (source.type !== 'S2S_REVERSE_DNS' && shownValues.has(source.value)) return false;
    return true;
  });

  return { registered, observed, shopperIp, message, tone, extras };
}

function IdRow({ label, value }: { label: string; value: string }) {
  const text = hasText(value) ? value : '';
  return (
    <div className="flex min-w-0 items-center gap-3 text-sm">
      <span className="w-[7.5rem] shrink-0 text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {text ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="min-w-0 flex-1 truncate text-right font-medium">{text}</span>
            </TooltipTrigger>
            <TooltipContent side="top" align="end" className={cn(TOOLTIP_HINT_CLASS, "max-w-sm break-all")}>
              {text}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="min-w-0 flex-1 truncate text-right font-medium">—</span>
        )}
        {text && <CopyButton value={text} label={label} />}
      </div>
    </div>
  );
}

export default function TransactionDetailDrawer({
  open,
  onOpenChange,
  order,
  isMerchantUser,
  loadingRelated,
  relatedTransactions,
  dccDetails,
}: TransactionDetailDrawerProps) {
  const origin = order ? buildOrigin(order) : null;
  const showRelated = !isMerchantUser && (loadingRelated || relatedTransactions.length > 0);
  const description = hasText(order?.description) ? order?.description : '';

  return (
    <Drawer
      open={open && !!order}
      onOpenChange={onOpenChange}
      direction="right"
      handleOnly
      shouldScaleBackground={false}
    >
      {order && origin && (
        <DrawerContent
          className={cn(
            'inset-x-auto inset-y-0 right-0 mt-0 flex h-full w-[min(48rem,100vw)] max-w-[48rem] flex-col gap-0 overflow-hidden rounded-none rounded-l-[10px] border-y-0 border-r-0 p-0',
            'max-sm:w-full max-sm:max-w-none max-sm:rounded-none',
            '[&>div:first-child]:hidden',
          )}
        >
          <DrawerHeader className="min-w-0 border-b border-border px-6 py-5 text-left">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-baseline gap-2">
                  <DrawerTitle className="text-2xl">
                    {formatCurrency(order.amount, order.currency)}
                  </DrawerTitle>
                  {order.currency && (
                    <span className="text-sm font-medium text-muted-foreground">{order.currency}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={order.status} variant="order" />
                  {order.testMode && <StatusBadge status variant="mode" />}
                </div>
                <DrawerDescription>
                  {order.date}
                  {hasText(order.paymentMethodLabel) ? ` · ${order.paymentMethodLabel}` : ''}
                </DrawerDescription>
              </div>
              <DrawerClose
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </DrawerClose>
            </div>

            <div className="mt-4 min-w-0 space-y-1.5 overflow-hidden rounded-lg border border-border bg-muted/20 px-4 py-3">
              <IdRow label="Transaction ID" value={order.transactionId} />
              <IdRow label="Order ID" value={order.id} />
            </div>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6">
            <Section title="Customer">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Name">{order.customerName}</Field>
                <Field label="Email">
                  {hasText(order.customerEmail) ? (
                    <CopyValue value={order.customerEmail} label="email">
                      <span>{order.customerEmail}</span>
                    </CopyValue>
                  ) : (
                    order.customerEmail
                  )}
                </Field>
                <Field label="Phone">{order.customerPhone}</Field>
              </div>
            </Section>

            <Section title="Payment">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Method">{order.paymentMethodLabel}</Field>
                <Field label="Card Brand">{order.cardType}</Field>
                <Field label="Card Number">{order.cardNumber}</Field>
                <Field label="Flow">{order.flowName}</Field>
              </div>
            </Section>

            {!isMerchantUser && (
              <Section title="Merchant & Acquirer">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Merchant">{order.merchantName}</Field>
                  <Field label="Merchant ID">{order.merchantId}</Field>
                  <Field label="Merchant Terminal">{order.merchantTerminalName}</Field>
                  <Field label="Acquirer">{order.acquirerName}</Field>
                  <Field label="Acquirer Code">{order.acquirerCode}</Field>
                  <Field label="Acquirer Terminal">{order.acquirerTerminalName}</Field>
                </div>
              </Section>
            )}

            <Section title="Order">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Amount">
                  {formatCurrency(order.amount, order.currency, { includeCode: true })}
                </Field>
                {order.acquirerAmount != null && (
                  <Field label="Amount sent to acquirer">
                    {formatCurrency(
                      order.acquirerAmount,
                      order.acquirerCurrency || order.currency,
                      { includeCode: true },
                    )}
                  </Field>
                )}
                <Field label="Date / Time">{order.date}</Field>
                <Field label="Country">{order.country}</Field>
                <Field label="Order Status">
                  {getOrderStatusLabel(order.rawStatus || order.status)}
                </Field>
                <Field label="Transaction Status">{formatEntityStatusLabel(order.transactionStatus)}</Field>
                {description && (
                  <Field label="Description" wide>
                    {description}
                  </Field>
                )}
              </div>
            </Section>

            {showRelated && (
              <Section title="Origin">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Registered (MID)">
                    <UrlList
                      urls={origin.registered}
                      empty="— (no approved URL on this terminal)"
                      copyLabel="registered MID"
                    />
                  </Field>
                  <Field label="Observed origin">
                    {origin.observed ? (
                      <CopyValue value={origin.observed} label="observed origin">
                        <a
                          href={origin.observed}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          <UrlText url={origin.observed} />
                        </a>
                      </CopyValue>
                    ) : (
                      <span className="font-normal text-muted-foreground">
                        — (no browser origin — e.g. direct S2S call)
                      </span>
                    )}
                  </Field>
                  <Field label="Caller IP">{order.callerIp}</Field>
                  <Field label="Shopper IP">{origin.shopperIp}</Field>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                  <div className="text-xs text-muted-foreground">Traced from</div>
                  <p
                    className={cn(
                      'mt-1 text-sm font-medium',
                      origin.tone === 'bad' && 'text-destructive',
                      origin.tone === 'ok' && 'text-emerald-600',
                    )}
                  >
                    {origin.message}
                  </p>
                  {origin.extras.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {origin.extras.map((source, index) => (
                        <Field
                          key={`${source.type}-${index}`}
                          label={TRACE_LABELS[source.type] ?? source.type}
                        >
                          {COPY_TRACE_TYPES.has(source.type) ? (
                            <CopyValue
                              value={source.value}
                              label={TRACE_LABELS[source.type] ?? source.type}
                            >
                              <span>{source.value}</span>
                            </CopyValue>
                          ) : (
                            source.value
                          )}
                        </Field>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}

            {showRelated && (
              <Section title="Related Transactions">
                {loadingRelated ? (
                  <div className="space-y-2" role="status" aria-live="polite">
                    <span className="sr-only">Loading related transactions</span>
                    {[0, 1].map((row) => (
                      <div
                        key={row}
                        className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-3"
                        aria-hidden
                      >
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                          <div className="h-2.5 w-1/3 animate-pulse rounded bg-muted" />
                        </div>
                        <div className="h-3.5 w-16 animate-pulse rounded bg-muted" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {relatedTransactions.map((tx, index) => {
                      const id = tx.transactionId || tx.id || `TXN-${index + 1}`;
                      const dcc = tx.transactionId ? dccDetails[tx.transactionId] : undefined;
                      const method = tx.paymentMethod
                        ? formatPaymentMethodLabel(tx.paymentMethod)
                        : 'Card';
                      return (
                        <div key={`${id}-${index}`} className="rounded-lg border border-border px-3 py-2.5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="text-sm font-medium break-all">{id}</div>
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {tx.status || 'Processed'}
                                <span aria-hidden> · </span>
                                {method}
                              </div>
                            </div>
                            <div className="shrink-0 text-sm font-semibold">
                              {formatCurrency(Number(tx.amount ?? 0), String(tx.currency ?? ''))}
                            </div>
                          </div>
                          {dcc && <DccDetailTable dcc={dcc} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            )}
          </div>
        </DrawerContent>
      )}
    </Drawer>
  );
}
